import {
  updateDoc,
  addDoc,
  collection,
  doc,
  getDoc,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { embedFields } from "./EmbedFields";
import { generatePDF } from "../components/Invoice/GenerateInvoice";
import { uploadCloudApi, encryptApi } from "./cloudApi";

export const getUserPublicKey = async (uid) => {
  if (!uid) {
    console.error("No UID provided to getUserPublicKey");
    return null;
  }

  try {
    const userDocRef = doc(db, "users", uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.error(`User with UID ${uid} not found`);
      return null;
    }

    const userData = userDocSnap.data();
    return userData.publicKey || null;
  } catch (error) {
    console.error("Error fetching user public key:", error);
    return null;
  }
};

export const calculateOfferExpiry = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  date.setDate(date.getDate() + 15);
  return date.toISOString().split("T")[0];
};

export const generateFilledPdf = async (bytes, fields) => {
  try {
    // load the document once
    const pdfDoc = await PDFDocument.load(bytes);
    pdfDoc.registerFontkit(fontkit);
    // stamp on all your textboxes, checkboxes & images
    await embedFields(pdfDoc, fields);

    // return new bytes to upload, download, preview...
    return await pdfDoc.save();
  } catch (error) {
    console.error(error);
  }
};

export async function savePdfToCloud(
  data,
  userId,
  cid,
  formType,
  templates,
  privateKey
) {
  try {
    // 1) get the raw ArrayBuffer from your PDF generator
    const arrayBuffer = await generatePDF(
      data,
      "save",
      cid,
      formType,
      templates,
      userId,
      privateKey
    );

    // 2) wrap it in a browser Blob
    const pdfBlob = new Blob([arrayBuffer], { type: "application/pdf" });

    const url = await uploadCloudApi(pdfBlob, userId, "doc", null);

    return url;
  } catch (error) {
    console.error("Failed to upload pdf", error);
  }
}

export const calculateInvoiceTotals = (
  items,
  { vatOption = "vat", vatMethod = "inclusive", vatRate = 0.12 } = {}
) => {
  // Convert vatRate to a decimal if it's provided as a percentage
  if (vatRate > 1) {
    vatRate = vatRate / 100;
  }

  // Calculate base totals from items - ensure values are numbers
  const totalSales = items.reduce((sum, item) => {
    const subTotal = Number(item.subTotal) || 0;
    return sum + subTotal;
  }, 0);

  const totalDiscount = items.reduce((sum, item) => {
    const discount = Number(item.discount) || 0;
    return sum + discount;
  }, 0);

  let vatableSales = 0;
  let vat = 0;
  let totalPayable = 0;

  if (vatOption !== "non-vat") {
    if (vatMethod === "exclusive") {
      // VAT exclusive: totalSales does not include VAT.
      vat = totalSales * vatRate;
      vatableSales = totalSales;
      totalPayable = totalSales + vat;
    } else {
      // VAT inclusive: totalSales already includes VAT.
      vatableSales = totalSales / (1 + vatRate);
      vat = totalSales - vatableSales;
      totalPayable = totalSales;
    }
  } else {
    // Non-VAT: no VAT is applied.
    vat = 0;
    vatableSales = totalSales;
    totalPayable = totalSales;
  }

  return {
    totalSales: parseFloat(totalSales.toFixed(2)),
    totalDiscount: parseFloat(totalDiscount.toFixed(2)),
    vatableSales: parseFloat(vatableSales.toFixed(2)),
    vat: parseFloat(vat.toFixed(2)),
    totalPayable: parseFloat(totalPayable.toFixed(2)),
  };
};

export const formatCurrency = (value) => {
  return value
    ? new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
      }).format(value)
    : "";
};

export const formatInvoiceNumber = (lastInvoiceNumber, formType) => {
  // const prefix = formType === "noc" ? "NC-" : "";
  let prefix = "";
  if (formType === "noc") {
    prefix = "NC-";
  } else if (formType === "trans") {
    prefix = "TL-";
  } else if (formType === "ack") {
    prefix = "AR-";
  } else if (formType === "secres") {
    prefix = "SR-";
  } else if (formType === "legal") {
    prefix = "LA-";
  } else {
    prefix = "";
  }
  const formattedCount = String(lastInvoiceNumber).padStart(6, "0");
  const twoDigitYear = new Date().getFullYear().toString().slice(-2);
  return `${prefix}${formattedCount}-${twoDigitYear}`;
};

export const getLastInvoiceNumber = async (templateId) => {
  if (!templateId) {
    return null;
  }
  try {
    const docRef = doc(db, "invoiceTemplates", templateId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // console.log(data.lastInvoiceNumber);
      return data.lastInvoiceNumber;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting last invoice number:", error);
    throw error;
  }
};

export const updateLastInvoiceNumber = async (id) => {
  try {
    const docRef = doc(db, "invoiceTemplates", id);
    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      if (!docSnap.exists()) {
        throw new Error("Document does not exist!");
      }
      const data = docSnap.data();
      // Convert the stored string to a number (default to 0 if not set)
      let currentNumber = parseInt(data.lastInvoiceNumber, 10) || 0;
      currentNumber++; // Increment the number

      // Update the document with the incremented value as a string
      transaction.update(docRef, {
        lastInvoiceNumber: currentNumber.toString(),
      });
    });
    // console.log("Last invoice number updated successfully.");
  } catch (error) {
    console.error("Error updating last invoice number:", error);
  }
};

export const encryptInvoice = async (data, recipients) => {
  try {
    const {
      from,
      to,
      createdBy,
      invoiceNo,
      date,
      header,
      templateRef,
      ...dataToSave
    } = data;

    // Encrypt the remaining data
    const encryptedData = await encryptApi(dataToSave, recipients);

    // Combine the removed fields and the encryptedData
    const encInvoice = {
      from: data.from,
      to: data.to,
      createdBy: data.createdBy,
      invoiceNo: data.invoiceNo,
      date: data.date,
      header: data.header,
      templateRef: data.templateRef,
      encryptedData,
    };

    return encInvoice;
  } catch (error) {
    console.error("Error Encrypting Receipt/Report:", error);
  }
};

async function encryptTemplate(data, recipients) {
  try {
    // Destructure the fields
    const {
      tempType,
      from,
      header,
      createdBy,
      lastInvoiceNumber,
      notarizationStatus,
      notarizationType,
      ...dataToSave
    } = data;

    // Encrypt the remaining data
    const encryptedData = await encryptApi(dataToSave, recipients);

    // Combine with the removed fields and the encryptedData
    const tempData = {
      tempType: data.tempType,
      createdBy: data.createdBy,
      from: data.from,
      header: data.header,
      lastInvoiceNumber: data.lastInvoiceNumber,
      encryptedData,
    };

    if (data.notarizationStatus) {
      tempData.notarizationStatus = data.notarizationStatus;
    }
    if (data.notarizationType) {
      tempData.notarizationType = data.notarizationType;
    }

    return tempData;
  } catch (error) {
    console.error("Error Encrypting Template:", error);
  }
}

export const saveOrUpdateTemplate = async (
  templateId,
  updatedFormData,
  isUpdate,
  ackType = null,
  recipients
) => {
  try {
    // console.log(ackType);
    const formData = { ...updatedFormData };

    // console.log(formData);

    // console.log(ackType);
    const uploadFields =
      ackType !== null
        ? ["logo", "footer"]
        : ["logo", "gcashQr", "gcashQr2", "alignedQRs"];

    const uploadPromises = uploadFields
      .map((field) => {
        if (formData[field] instanceof File) {
          return uploadCloudApi(
            formData[field],
            formData.createdBy,
            "img"
          ).then((url) => ({ field, url }));
        }
        return null;
      })
      .filter(Boolean);

    const uploadResults = await Promise.all(uploadPromises);

    uploadResults.forEach(({ field, url }) => {
      formData[field] = url;
    });

    // ✅ Start with base template data
    const templateData = {
      lastInvoiceNumber: formData.invoiceNo || 0,
      header: formData.header || "",
      logo: formData.logo || null,
      from: {
        name: formData.from?.name || "",
        address: formData.from?.address || "",
        contact: formData.from?.contact || "",
      },
      createdBy: formData.createdBy || "",
      tableHeaderColor: formData.tableHeaderColor,
      tableHeaderTextColor: formData.tableHeaderTextColor,
      tempType: formData.tempType,
    };

    // Conditionally add fields based on ackType
    if (ackType === null) {
      Object.assign(templateData, {
        headerDescription: formData.headerDescription || "",
        cashDetails: formData.cashDetails || "",
        checkDetails: formData.checkDetails || "",
        gcashQr: formData.gcashQr || null,
        gcashQr2: formData.gcashQr2 || null,
        vatMethod: formData.vatMethod || "vat",
        vatOption: formData.vatOption || "inclusive",
        taxCode: formData.taxCode || "",
        vatRate: formData.vatRate || 12,
        alignedQRs: formData.alignedQRs || null,
        qrAlignment: formData.qrAlignment || null,
        headerColor: formData.headerColor,
      });
    } else if (ackType === "ack" || ackType === "secres") {
      Object.assign(templateData, {
        notarizationStatus: formData.notarizationStatus,
        notarizationType: formData.notarizationType || "",
        footer: formData.footer || null,
        body1: formData.body1 || "",
        body2: formData.body2 || "",
      });
    } else if (ackType === "trans" || ackType === "legal") {
      Object.assign(templateData, {
        footer: formData.footer || null,
        body1: formData.body1 || "",
        body2: formData.body2 || "",
      });
    }

    const encryptedTemplate = await encryptTemplate(templateData, recipients);

    // console.log(encryptedTemplate);
    // If ackType === "trans", we include neither — so no changes needed

    // console.log(templateData);
    // ✅ Save to Firestore
    let templateRef;
    if (isUpdate && templateId) {
      templateRef = doc(db, "invoiceTemplates", templateId);
      await updateDoc(templateRef, encryptedTemplate);
    } else {
      templateRef = await addDoc(
        collection(db, "invoiceTemplates"),
        encryptedTemplate
      );
      templateId = templateRef.id;
    }

    return { ...encryptedTemplate, id: templateId };
  } catch (error) {
    console.error("Error saving or updating template:", error);
    throw new Error("Failed to save or update the template. Please try again.");
  }
};

export const wrapTextInParagraphs = (text) => {
  return text
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => `<p>${line.trim()}</p>`)
    .join("");
};

export const mailForm = (emailMessage) => {
  const mailBody = wrapTextInParagraphs(emailMessage);
  const body = `
  <h1>Good day!</h1>
  ${mailBody}

  <span>If you notice any problems with the invoice attachment or have any questions, please feel free to get in touch with us via the platforms provided below.</span>
  <p></p>
  <span>Note: This is an automated message. Please do not respond to this email.</span>
  `;

  return emailTemplate(body);
};

const emailTemplate = (bodyContent) => {
  return `
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Email Template</title>
    <style>
      body {
        margin: 0;
        padding: 0;
        font-family: Arial, sans-serif;
        background-color: #f3f3f3;
      }
      .email-container {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        background-color: #ffffff;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      }
      .header {
        background-color: #144448;
        padding: 20px;
        color: white;
        text-align: center;
      }
      .header img {
        max-width: 120px;
      }
      .content {
        padding: 30px;
        text-align: left;
      }
      .content h1 {
        font-size: 24px;
        margin-bottom: 10px;
        color: #3d0066; 
      }
      .content p {
        font-size: 16px;
        color: #777777;
        line-height: 1.6;
        margin-bottom: 20px;
      }
      .content img {
        max-width: 220px;
        max-height: 220px;
      }
      .content blockquote {
        font: 14px/22px normal helvetica, sans-serif;
        margin-top: 10px;
        margin-bottom: 10px;
        margin-left: 60px;
        background-color: #f3f3f3;
        padding: 15px;
        text-align: left; 
      }
      .content table {
        font-family: arial, sans-serif;
        border-collapse: collapse;
        width: 100%;
      }
      .content th {
        background-color: #f3f3f3;
      }
      .content td, th {
        border: 1px solid #dddddd;
        text-align: left;
        padding: 8px;
      }
      .content span {
        font-size: 0.8rem;
        font-style: italic;
        color: #808080;
        line-height: 1.6;
        margin-bottom: 15px;
      }
      .footer {
        padding: 20px;
        background-color: #f9f9f9;
        text-align: center;
        color: #999999;
        font-size: 14px;
      }
      .footer a {
        color: #007bff;
        text-decoration: none;
        margin: 0 10px;
      }
      .social-icons {
        margin-top: 10px;
      }
      .social-icons a {
        color: #3d0066; /* Set icon color */
        text-decoration: none;
        margin: 0 5px;
        display: inline-block;
      }
      .social-icons img {
        width: 24px;
      }
    </style>
  </head>
  <body>
    <div class="email-container">
      <!-- Header Section -->
      <div class="header">
        <img src="${process.env.REACT_APP_LOGO_MAIL_URL}" alt="Logo">
      </div>

      <!-- Content Section -->
      <div class="content">
        ${bodyContent} 
      </div>

      <!-- Footer Section -->
      <div class="footer">
        <p><strong>Underground Project Business Hub & Cafe</strong>, <br/>111 Centro Maximo Bldg. Cogon-Ramos, Cebu City, <br/>Cebu 6000, PH</p>
        
        <!-- Social Icons -->
        <div class="social-icons">
          <a href="https://www.facebook.com/UndergroundProjectCebu?_rdr">
            <img src="${process.env.REACT_APP_FB_MAIL_URL}" alt="Facebook">
          </a>
        </div>

      </div>
    </div>
  </body>
  </html>
  `;
};

export const getFileAsBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper to fetch image and convert it to a base64 data URL
export const getBase64ImageFromUrl = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous"); // Allow cross-origin if needed
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };
    img.onerror = (error) => reject(error);
    img.src = url;
  });
};

export const sanitizeQuillHtml = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html;

  function cleanNode(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // Remove class attribute
      node.removeAttribute("class");

      // Remove all data- attributes
      Array.from(node.attributes).forEach((attr) => {
        if (attr.name.startsWith("data-")) {
          node.removeAttribute(attr.name);
        }
      });

      // Clean up the style attribute: allow only font-size, font-weight, and font-style
      if (node.hasAttribute("style")) {
        const style = node.style;
        let allowedStyles = "";
        if (style.fontSize) {
          allowedStyles += `font-size: ${style.fontSize}; `;
        }
        if (style.fontWeight) {
          allowedStyles += `font-weight: ${style.fontWeight}; `;
        }
        if (style.fontStyle) {
          allowedStyles += `font-style: ${style.fontStyle}; `;
        }
        if (allowedStyles) {
          node.setAttribute("style", allowedStyles.trim());
        } else {
          node.removeAttribute("style");
        }
      }

      // Process child nodes recursively
      Array.from(node.childNodes).forEach((child) => cleanNode(child));
    }
  }

  Array.from(container.childNodes).forEach((child) => cleanNode(child));
  return container.innerHTML;
};

export const updateUserTheme = (userSettings) => {
  const root = document.documentElement;

  // Update main color variables
  root.style.setProperty("--brand-primary", userSettings.primaryColor);
  root.style.setProperty("--brand-secondary", userSettings.secondaryColor);
  root.style.setProperty("--brand-dark-alt", userSettings.darkAlt);
  root.style.setProperty("--brand-color", userSettings.color);
  root.style.setProperty("--brand-altColor", userSettings.altColor);
  // Update logo and brand name if they exist in the DOM
  const logoElement = document.getElementById("brandLogo");
  if (logoElement && userSettings.logoUrl) {
    logoElement.src = userSettings.logoUrl;
  }

  const brandNameElement = document.getElementById("brandName");
  if (brandNameElement && userSettings.brandName) {
    brandNameElement.innerText = userSettings.brandName;
  }

  // Check if the user wants to use a background color instead of the default image
  if (userSettings.backgroundColor) {
    // Set the custom background color and disable the background image
    root.style.setProperty("--custom-bg-color", userSettings.backgroundColor);
    root.style.setProperty("--custom-bg-image", "none");
  } else {
    // No custom background color provided; revert to default styles
    root.style.setProperty("--custom-bg-color", "transparent");
    root.style.setProperty("--custom-bg-image", "url('/assets/green-bg.webp')");
  }

  // console.log("Theme updated with:", userSettings);
};

export const revertTheme = () => {
  const defaultTheme = {
    primaryColor: "#134448",
    secondaryColor: "#cca85c",
    darkAlt: "#ffd65a",
    color: "#ffffff",
    altColor: "#d3d3d3",
    backgroundColor: null,
    logoUrl: null,
    brandName: "",
  };

  updateUserTheme(defaultTheme);
};

export const themeColors = () => [
  {
    id: "1001",
    name: "Default",
    primaryColor: "#134448",
    secondaryColor: "#cca85c",
    darkAlt: "#ffd65a",
    color: "#ffffff",
    altColor: "#d3d3d3",
  },
  {
    id: "1002",
    name: "Black & White",
    primaryColor: "#121212",
    secondaryColor: "#CACACA",
    darkAlt: "#606470",
    backgroundColor: "#F5F7F8",
    color: "#343434",
  },
  {
    id: "1003",
    name: "Ocean",
    primaryColor: "#0077b6",
    secondaryColor: "#90e0ef",
    darkAlt: "#023e8a",
    backgroundColor: "#FBFBFB",
    color: "#03045e",
  },
  {
    id: "1004",
    name: "Fanta",
    primaryColor: "#FC8F54",
    secondaryColor: "#FDE7BB",
    darkAlt: "#AA5486",
    backgroundColor: "#fff5ee",
    color: "#4b0082",
  },
  {
    id: "1005",
    name: "Lemon Green",
    primaryColor: "#3A7D44",
    secondaryColor: "#FFD65A",
    darkAlt: "#FF9D23",
    backgroundColor: "#F8F5E9",
    color: "#3A7D44",
  },
  {
    id: "1006",
    name: "Black & Gold",
    primaryColor: "#212121",
    secondaryColor: "#cca85c",
    darkAlt: "#FEC260",
    backgroundColor: "#FBFBFB",
    color: "#45474B",
  },
  {
    id: "1007",
    name: "Candy",
    primaryColor: "#2456ab",
    secondaryColor: "#FF69B4",
    darkAlt: "#FF76CE",
    backgroundColor: "#f0cdd4",
    color: "#304463",
  },
];

export const findExistingClient = (clientData, existingClients) => {
  if (!clientData.name || clientData.name.trim() === "") {
    return null;
  }

  return (
    existingClients?.find(
      (existingClient) =>
        existingClient.name.trim().toLowerCase() ===
        clientData.name.trim().toLowerCase()
    ) || null
  );
};

export const generateNextCID = (existingClients) => {
  if (!existingClients || existingClients.length === 0) {
    return "CID00001";
  }

  // Find the highest existing CID number
  const highestCID = existingClients.reduce((max, client) => {
    if (client.cid && client.cid.startsWith("CID")) {
      const num = parseInt(client.cid.substring(3), 10);
      return num > max ? num : max;
    }
    return max;
  }, 0);

  // Generate next CID with 5-digit zero-padding
  const nextNumber = highestCID + 1;
  return `CID${nextNumber.toString().padStart(5, "0")}`;
};

export const hasClientDataChanged = (newData, existingClient) => {
  return (
    (newData.address?.trim() || "") !==
      (existingClient.address?.trim() || "") ||
    (newData.tinNo?.trim() || "") !== (existingClient.tinNo?.trim() || "") ||
    (newData.contact?.trim() || "") !==
      (existingClient.contact?.trim() || "") ||
    (newData.rep?.trim() || "") !== (existingClient.rep?.trim() || "") ||
    (newData.email?.trim() || "") !== (existingClient.email?.trim() || "")
  );
};

export function numberToWords(n) {
  const a = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const b = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  if (n === 0) return "zero";
  if (n < 20) return a[n];
  if (n < 100)
    return b[Math.floor(n / 10)] + (n % 10 !== 0 ? "-" + a[n % 10] : "");
  if (n < 1000)
    return (
      a[Math.floor(n / 100)] +
      " hundred" +
      (n % 100 !== 0 ? " " + numberToWords(n % 100) : "")
    );
  if (n === 1000) return "one thousand";

  return n.toString(); // fallback for larger numbers
}
